import i18n from 'i18next'
import * as Yup from 'yup'
import { convertCleaveFormat } from '../../../utils/validations'
import { REAL_ESTATE_TYPES } from '../../constants'
import responseFields from '../../constants/responseFields'
import { companyResponseFields } from '../../response/company'
import { contactResponseFields } from '../../response/contact'
import { companyGeneralFields, companyOtherInfoFields, companyProfileFields } from './fields'

export const companyGeneralValidation = (data) => {
    return {
        initialValues: {
            id: data?.internalAppId ?? '', // internalAppId is used for display, id is sent in prepareModelForSubmit
            [companyGeneralFields.NAME]: data?.[companyResponseFields.CONTACT_COMPANY]?.name ?? '',
            [companyGeneralFields.FULL_NAME]: data?.[companyResponseFields.CONTACT_COMPANY]?.fullName ?? '',
            [companyGeneralFields.CONTACT_LEGAL_FORM_ID]:
                data?.[companyResponseFields.CONTACT_COMPANY]?.[companyResponseFields.CONTACT_LEGAL_FORM]?.id ?? '',
            [companyGeneralFields.TAX_NUMBER]: data?.taxNumber ?? '',
            [companyGeneralFields.INITIAL_CONTACT_DATE]: data?.initialContactDate ?? '',
            [companyGeneralFields.CONTACT_PRIORITY_ID]: data?.contactPriority?.id ?? '',
            [companyGeneralFields.DATA_SOURCE_ID]: data?.dataSource?.id ?? '',
            contact_phone_group: data?.contactPhone?.length
                ? data?.contactPhone?.map((item) => ({
                      [companyGeneralFields.PHONE_NUMBER]: item?.phoneNumber ?? '',
                      [companyGeneralFields.CONTACT_PHONE_TYPE_ID]: item?.contactPhoneType?.id ?? '',
                  }))
                : [
                      {
                          [companyGeneralFields.PHONE_NUMBER]: '',
                          [companyGeneralFields.CONTACT_PHONE_TYPE_ID]: '',
                      },
                  ],
            contact_email_group: data?.contactEmail?.length
                ? data?.contactEmail?.map((item) => ({
                      [companyGeneralFields.EMAIL]: item?.email ?? '',
                      [companyGeneralFields.CONTACT_EMAIL_TYPE_ID]: item?.contactEmailType?.id ?? '',
                  }))
                : [
                      {
                          [companyGeneralFields.EMAIL]: '',
                          [companyGeneralFields.CONTACT_EMAIL_TYPE_ID]: '',
                      },
                  ],
            [companyGeneralFields.WEBSITE]: data?.contactCompany?.website ?? '',
            [companyGeneralFields.STREET]: data?.street ?? '',
            [companyGeneralFields.STREET_NUMBER]: data?.streetNumber ?? '',
            [companyGeneralFields.CONTACT_COUNTRY_ID]: data?.contactCountry?.id ?? '',
            [companyGeneralFields.CITY]: data?.city ?? '',
            [companyGeneralFields.ZIP_CODE]: data?.zipCode ?? '',
            [companyGeneralFields.CONTACT_STATUS_ID]: data?.contactStatus?.id?.toString() ?? '',
            [companyGeneralFields.AGENT_ID]: data?.agent?.id ?? '',
            [companyGeneralFields.CONTACT_QUERY_CATEGORY_ID]: data?.contactQueryCategory?.id?.toString() ?? '',
        },
        validationSchema: Yup.object({
            [companyGeneralFields.NAME]: Yup.string().required(i18n.t('validation.message')),
            [companyGeneralFields.FULL_NAME]: Yup.string(),
            [companyGeneralFields.CONTACT_LEGAL_FORM_ID]: Yup.string(),
            [companyGeneralFields.TAX_NUMBER]: Yup.string(),
            [companyGeneralFields.WEBSITE]: Yup.string().url(i18n.t('validation.url')),
            [companyGeneralFields.INITIAL_CONTACT_DATE]: Yup.string(),
            [companyGeneralFields.CONTACT_PRIORITY_ID]: Yup.string(),
            [companyGeneralFields.DATA_SOURCE_ID]: Yup.string(),
            [companyGeneralFields.CONTACT_STATUS_ID]: Yup.string().required(i18n.t('validation.message')),
            contact_phone_group: Yup.array().of(
                Yup.object().shape({
                    [companyGeneralFields.PHONE_NUMBER]: Yup.string(),
                    [companyGeneralFields.CONTACT_PHONE_TYPE_ID]: Yup.string(),
                })
            ),
            contact_email_group: Yup.array().of(
                Yup.object().shape({
                    [companyGeneralFields.EMAIL]: Yup.string().email(i18n.t('validation.email')),
                    [companyGeneralFields.CONTACT_EMAIL_TYPE_ID]: Yup.string(),
                })
            ),
            [companyGeneralFields.STREET]: Yup.string(),
            [companyGeneralFields.STREET_NUMBER]: Yup.string(),
            [companyGeneralFields.CONTACT_COUNTRY_ID]: Yup.string(),
            [companyGeneralFields.CITY]: Yup.string(),
            [companyGeneralFields.ZIP_CODE]: Yup.string(),
            [companyGeneralFields.AGENT_ID]: Yup.string().required(i18n.t('validation.message')),
        }),
        prepareModelForSubmit: (values) => {
            return {
                ...values,
                [companyGeneralFields.ID]: data?.id ?? '',
                contact_email_group: values.contact_email_group?.some((x) => x.email || x.contact_email_type_id) ? values.contact_email_group : [],
                contact_phone_group: values.contact_phone_group?.some(
                    (x) => x[companyGeneralFields.PHONE_NUMBER] || x[companyGeneralFields.CONTACT_PHONE_TYPE_ID]
                )
                    ? values.contact_phone_group
                    : [],
            }
        },
    }
}

export const companyOtherInfoValidation = (data) => {
    return {
        initialValues: {
            [companyOtherInfoFields.CONTACT_CORE_BUSINESS_ID]:
                data?.[companyResponseFields.CONTACT_COMPANY]?.[companyResponseFields.CONTACT_CORE_BUSINESS]?.id ?? '',
            [companyOtherInfoFields.CONTACT_COMPANY_SIZE_ID]: data?.[companyResponseFields.CONTACT_COMPANY]?.contactCompanySize?.id ?? '',
            [companyOtherInfoFields.EMPLOYEES_NO]: data?.[companyResponseFields.CONTACT_COMPANY]?.employeesNo ?? '',
            [companyOtherInfoFields.BANK_ACCOUNT_NO]: data?.contactCompany?.bankAccountNo ?? '',
            [companyOtherInfoFields.SHARE_CAPITAL]: data?.[companyResponseFields.CONTACT_COMPANY]?.shareCapital ?? '',
            [companyOtherInfoFields.REGISTRY_COURT]: data?.[companyResponseFields.CONTACT_COMPANY]?.registryCourt ?? '',
            [companyOtherInfoFields.NOTES]: data?.notes ?? '',
        },
        validationSchema: Yup.object({
            [companyOtherInfoFields.CONTACT_CORE_BUSINESS_ID]: Yup.number(),
            [companyOtherInfoFields.CONTACT_COMPANY_SIZE_ID]: Yup.number(),
            [companyOtherInfoFields.EMPLOYEES_NO]: Yup.number(),
            [companyOtherInfoFields.BANK_ACCOUNT_NO]: Yup.string(),
            [companyOtherInfoFields.REGISTRY_COURT]: Yup.string(),
            [companyOtherInfoFields.SHARE_CAPITAL]: Yup.string(),
            [companyOtherInfoFields.NOTES]: Yup.string(),
        }),
    }
}

export const companyProfileValidation = (data, propertyTypeValue, featureFlags) => {
    const propertyType = Number(propertyTypeValue)
    const { house, flat, officeSpace, land, garage } = REAL_ESTATE_TYPES

    const isHouse = propertyType === house
    const isFlat = propertyType === flat
    const isOffice = propertyType === officeSpace
    const isLand = propertyType === land
    const isGarage = propertyType === garage
    const isRooms = [house, flat, officeSpace].includes(propertyType)
    const isBedrooms = [house, flat].includes(propertyType)

    return {
        initialValues: {
            id: data?.id ?? '',
            [companyProfileFields.CONTACT_PROFILE_SERVICE_ID]: data?.[contactResponseFields.CONTACT_PROFILE_SERVICE]?.id ?? '',
            [companyProfileFields.PROPERTY_TYPE_ID]: data?.propertyType?.id ?? '',
            [companyProfileFields.PROPERTY_SUB_TYPE_ID]: data?.propertySubType?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.CONTACT_PROFILE_STATUS_ID]: data?.[contactResponseFields.CONTACT_PROFILE_STATUS]?.id?.toString() ?? '',
            [companyProfileFields.CONTACT_FINANCING_ID]: data?.[contactResponseFields.CONTACT_FINANCING]?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.CONTACT_PROFILE_DURATION_ID]: data?.[contactResponseFields.CONTACT_PROFILE_DURATION]?.id?.toString() ?? '',
            [companyProfileFields.COMPANY_DEFINITION_ID]: data?.[contactResponseFields.COMPANY_DEFINITION]?.id?.toString() ?? '',
            [companyProfileFields.COUNTRY_ID]: data?.[contactResponseFields.COUNTRY]?.id?.toString() ?? '',
            [companyProfileFields.COUNTY_ID]: data?.[contactResponseFields.COUNTY]?.id?.toString() ?? '',
            [companyProfileFields.WIDER_AREA_ID]: data?.[contactResponseFields.WIDER_AREA]?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.CITY_ID]: data?.[contactResponseFields.CITY]?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.QUARTER_ID]: data?.[contactResponseFields.QUARTER]?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.ISLAND_ID]: data?.[contactResponseFields.ISLAND]?.map((item) => item.id?.toString()) ?? [],
            [companyProfileFields.PRICE_FROM]: data?.[contactResponseFields.PRICE_FROM] ?? '',
            [companyProfileFields.PRICE_TO]: data?.[contactResponseFields.PRICE_TO] ?? '',
            ...(isRooms
                ? {
                      [companyProfileFields.ROOMS_FROM]: data?.[contactResponseFields.ROOMS_FROM] ?? '',
                      [companyProfileFields.ROOMS_TO]: data?.[contactResponseFields.ROOMS_TO] ?? '',
                  }
                : {}),
            ...(isBedrooms
                ? {
                      [companyProfileFields.BEDROOMS_FROM]: data?.[contactResponseFields.BEDROOMS_FROM] ?? '',
                      [companyProfileFields.BEDROOMS_TO]: data?.[contactResponseFields.BEDROOMS_TO] ?? '',
                  }
                : {}),
            [companyProfileFields.SPECIAL_REQUEST]: data?.[contactResponseFields.SPECIAL_REQUEST] ?? '',
            [companyProfileFields.AREA_FROM]: data?.[contactResponseFields.AREA_FROM] ?? '',
            [companyProfileFields.AREA_TO]: data?.[contactResponseFields.AREA_TO] ?? '',
            [companyProfileFields.CONSTRUCTION_YEAR_FROM]: data?.[contactResponseFields.CONSTRUCTION_YEAR_FROM] ?? '',
            [companyProfileFields.CONSTRUCTION_YEAR_TO]: data?.[contactResponseFields.CONSTRUCTION_YEAR_TO] ?? '',
            ...(isFlat || isOffice
                ? {
                      [companyProfileFields.PROPERTY_FLOOR_POSITION_ID]:
                          data?.[contactResponseFields.PROPERTY_FLOOR_POSITION]?.map((item) => item.id?.toString()) ?? [],
                      [companyProfileFields.FLOOR_NO_FROM]: data?.[contactResponseFields.FLOOR_FROM] ?? '',
                      [companyProfileFields.FLOOR_NO_TO]: data?.[contactResponseFields.FLOOR_TO] ?? '',
                  }
                : {}),
            ...(isFlat
                ? {
                      [companyProfileFields.EXCLUDE_BASEMENT]: data?.[contactResponseFields.EXCLUDE_BASEMENT] ?? false,
                      [companyProfileFields.EXCLUDE_LAST_FLOOR]: data?.[contactResponseFields.EXCLUDE_LAST_FLOOR] ?? false,
                  }
                : {}),
            ...(isRooms
                ? {
                      [companyProfileFields.PROPERTY_SPACE_ID]: data?.[companyResponseFields.PROPERTY_SPACE]?.map((x) => x.id.toString()) ?? [],
                  }
                : {}),
            ...(!isGarage
                ? {
                      [companyProfileFields.PROPERTY_DESCRIPTIONS_ID]:
                          data?.[contactResponseFields.PROPERTY_DESCRIPTIONS]?.map((item) => item.id?.toString()) ?? [],
                      [companyProfileFields.PROPERTY_UTILITY_ID]:
                          data?.[contactResponseFields.PROPERTY_UTILITY]?.map((item) => item.id?.toString()) ?? [],
                  }
                : {}),
            ...(!isGarage && !isLand
                ? {
                      [companyProfileFields.PROPERTY_EQUIPMENT_ID]:
                          data?.[contactResponseFields.PROPERTY_EQUIPMENT]?.map((item) => item.id?.toString()) ?? [],
                  }
                : {}),
            ...(!isGarage && !isLand
                ? {
                      [companyProfileFields.PROPERTY_HEATING_ID]:
                          data?.[contactResponseFields.PROPERTY_HEATING]?.map((item) => item.id?.toString()) ?? [],
                  }
                : {}),
            ...(isHouse
                ? {
                      [companyProfileFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
                  }
                : {}),
            ...(featureFlags?.automatic_offer_sending
                ? {
                      [companyProfileFields.AUTOMATIC_OFFER_ENABLED]: data?.[contactResponseFields.AUTOMATIC_OFFER_ENABLED] ?? false,
                  }
                : {}),
        },
        validationSchema: Yup.object({
            [companyProfileFields.PROPERTY_TYPE_ID]: Yup.string().required(i18n.t('validation.message')),
            [companyProfileFields.CONTACT_PROFILE_SERVICE_ID]: Yup.string().required(i18n.t('validation.message')),
            [companyProfileFields.CONTACT_PROFILE_STATUS_ID]: Yup.number(),
            [companyProfileFields.CONTACT_FINANCING_ID]: Yup.array().of(Yup.string()),
            [companyProfileFields.CONTACT_PROFILE_DURATION_ID]: Yup.number(),
            [companyProfileFields.CONTACT_DEFINITION_ID]: Yup.number(),
            [companyProfileFields.COUNTRY_ID]: Yup.number(),
            [companyProfileFields.PRICE_FROM]: Yup.number().transform(convertCleaveFormat),
            [companyProfileFields.PRICE_TO]: Yup.number()
                .transform(convertCleaveFormat)
                .when(companyProfileFields.PRICE_FROM, {
                    is: (val) => val != null && val !== '',
                    then: (schema) => schema.moreThan(Yup.ref(companyProfileFields.PRICE_FROM), i18n.t('validation.moreThan')),
                }),
            [companyProfileFields.AREA_FROM]: Yup.number().transform(convertCleaveFormat),
            [companyProfileFields.AREA_TO]: Yup.number()
                .transform(convertCleaveFormat)
                .when(companyProfileFields.AREA_FROM, {
                    is: (val) => val != null && val !== '',
                    then: (schema) => schema.moreThan(Yup.ref(companyProfileFields.AREA_FROM), i18n.t('validation.moreThan')),
                }),
            [companyProfileFields.ROOMS_FROM]: Yup.number(),
            [companyProfileFields.ROOMS_TO]: Yup.number().when(companyProfileFields.ROOMS_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(companyProfileFields.ROOMS_FROM), i18n.t('validation.moreThan')),
            }),
            [companyProfileFields.BEDROOMS_FROM]: Yup.number(),
            [companyProfileFields.BEDROOMS_TO]: Yup.number().when(companyProfileFields.BEDROOMS_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(companyProfileFields.BEDROOMS_FROM), i18n.t('validation.moreThan')),
            }),
            [companyProfileFields.FLOOR_NO_FROM]: Yup.number(),
            [companyProfileFields.FLOOR_NO_TO]: Yup.number().when(companyProfileFields.FLOOR_NO_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(companyProfileFields.FLOOR_NO_FROM), i18n.t('validation.moreThan')),
            }),
            [companyProfileFields.CONSTRUCTION_YEAR_FROM]: Yup.number(),
            [companyProfileFields.CONSTRUCTION_YEAR_TO]: Yup.number().when(companyProfileFields.CONSTRUCTION_YEAR_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.min(Yup.ref(companyProfileFields.CONSTRUCTION_YEAR_FROM), i18n.t('validation.moreThan')),
            }),
        }),
        prepareModelForSubmit: (values) => {
            const { [companyProfileFields.AUTOMATIC_OFFER_ENABLED]: automaticOfferEnabled, ...rest } = values

            return {
                ...rest,
                ...(featureFlags?.automatic_offer_sending
                    ? { [companyProfileFields.AUTOMATIC_OFFER_ENABLED]: Boolean(automaticOfferEnabled) }
                    : {}),
                [companyProfileFields.PRICE_FROM]: values?.[companyProfileFields.PRICE_FROM]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [companyProfileFields.PRICE_TO]: values?.[companyProfileFields.PRICE_TO]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [companyProfileFields.AREA_FROM]: values?.[companyProfileFields.AREA_FROM]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [companyProfileFields.AREA_TO]: values?.[companyProfileFields.AREA_TO]?.toString().replace(/\./g, '').replace(/,/g, '.'),
            }
        },
    }
}

export const companyDetailSearchValidation = (data) => {
    return {
        initialValues: {
            [responseFields.NAME]: data?.[responseFields.NAME] ?? '',
            [responseFields.CONTACT_PHONE]: data?.[responseFields.CONTACT_PHONE] ?? '',
            [responseFields.CONTACT_EMAIL]: data?.[responseFields.CONTACT_EMAIL] ?? '',
            [responseFields.AGENT]: data?.[responseFields.AGENT] ?? '',
            [responseFields.INITIAL_CONTACT_DATE_FROM]: data?.[responseFields.INITIAL_CONTACT_DATE_FROM] ?? '',
            [responseFields.INITIAL_CONTACT_DATE_TO]: data?.[responseFields.INITIAL_CONTACT_DATE_TO] ?? '',
            [responseFields.LAST_ACTIVITY_DATE_FROM]: data?.[responseFields.LAST_ACTIVITY_DATE_FROM] ?? '',
            [responseFields.LAST_ACTIVITY_DATE_TO]: data?.[responseFields.LAST_ACTIVITY_DATE_TO] ?? '',
            [responseFields.CONTACT_COUNTRY]: data?.[responseFields.CONTACT_COUNTRY] ?? '',
            [responseFields.CITY]: data?.[responseFields.CITY] ?? [],
            [responseFields.CONTACT_PROFILE_SERVICE]: data?.contactProfile?.[responseFields.CONTACT_PROFILE_SERVICE] ?? '',
            [responseFields.CONTACT_PROFILE_STATUS]: data?.contactProfile?.[responseFields.CONTACT_PROFILE_STATUS] ?? '',
            [responseFields.CONTACT_QUERY_CATEGORY]: data?.[responseFields.CONTACT_QUERY_CATEGORY] ?? '',
            [responseFields.DATA_SOURCE]: data?.[responseFields.DATA_SOURCE] ?? '',
            [responseFields.CONTACT_PRIORITY]: data?.[responseFields.CONTACT_PRIORITY] ?? '',
            [responseFields.CONTACT_STATUS]: data?.[responseFields.CONTACT_STATUS],
        },
        formGroup: 'contact',
    }
}

export const companyProfileDetailSearchValidation = (data) => {
    return {
        initialValues: {
            [responseFields.PROPERTY_TYPE]: data?.contactProfile?.[responseFields.PROPERTY_TYPE] ?? [],
            [responseFields.PROPERTY_SUB_TYPE]: data?.contactProfile?.[responseFields.PROPERTY_SUB_TYPE] ?? [],
            [responseFields.PRICE_FROM]: data?.contactProfile?.[responseFields.PRICE_FROM] ?? '',
            [responseFields.PRICE_TO]: data?.contactProfile?.[responseFields.PRICE_TO] ?? '',
            [responseFields.ROOMS_FROM]: data?.contactProfile?.[responseFields.ROOMS_FROM] ?? '',
            [responseFields.ROOMS_TO]: data?.contactProfile?.[responseFields.ROOMS_TO] ?? '',
            [responseFields.BEDROOMS_FROM]: data?.contactProfile?.[responseFields.BEDROOMS_FROM] ?? '',
            [responseFields.BEDROOMS_TO]: data?.contactProfile?.[responseFields.BEDROOMS_TO] ?? '',
            [responseFields.AREA_FROM]: data?.contactProfile?.[responseFields.AREA_FROM] ?? '',
            [responseFields.AREA_TO]: data?.contactProfile?.[responseFields.AREA_TO] ?? '',
            [responseFields.FLOOR_FROM]: data?.contactProfile?.[responseFields.FLOOR_FROM] ?? '',
            [responseFields.FLOOR_TO]: data?.contactProfile?.[responseFields.FLOOR_TO] ?? '',
            [responseFields.CONTACT_PROFILE_DURATION]: data?.contactProfile?.[responseFields.CONTACT_PROFILE_DURATION] ?? '',
            [responseFields.COUNTRY]: data?.contactProfile?.[responseFields.COUNTRY] ?? '',
            [responseFields.COUNTY]: data?.contactProfile?.[responseFields.COUNTY] ?? [],
            [responseFields.CITY]: data?.contactProfile?.[responseFields.CITY] ?? [],
            [responseFields.WIDER_AREA]: data?.contactProfile?.[responseFields.WIDER_AREA] ?? [],
            [responseFields.QUARTER]: data?.contactProfile?.[responseFields.QUARTER] ?? [],
            [responseFields.PROPERTY_FLOOR]: data?.contactProfile?.[responseFields.PROPERTY_FLOOR] ?? [],
        },
        formGroup: 'contactProfile',
    }
}
